import { Injectable, inject, effect } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs/operators";
import posthog from "posthog-js";
import { environment } from "./environment";
import { AuthService } from "./auth.service";

export type AnalyticsEvent =
	| "dash_viewed"
	| "dash_purchased"
	| "game_lobby_viewed"
	| "game_started"
	| "game_ended"
	| "round_unlocked"
	| "round_completed"
	| "photo_submitted"
	| "results_viewed"
	| "team_joined"
	| "active_session_resumed"
	| "purchase_initiated"
	| "purchase_succeeded"
	| "purchase_failed";

@Injectable({ providedIn: "root" })
export class AnalyticsService {
	private readonly auth = inject(AuthService);
	private readonly router = inject(Router);
	private initialized = false;

	constructor() {
		this.init();
		this.trackPageViews();
		this.trackAuthChanges();
	}

	private init(): void {
		if (this.initialized) return;
		posthog.init(environment.posthogKey, {
			api_host: environment.posthogHost,
			capture_pageview: false,
			capture_pageleave: true,
			persistence: "localStorage",
			autocapture: false,
		});
		this.initialized = true;
	}

	private trackPageViews(): void {
		this.router.events
			.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
			.subscribe((e) => {
				posthog.capture("$pageview", { $current_url: e.urlAfterRedirects });
			});
	}

	private trackAuthChanges(): void {
		effect(() => {
			const user = this.auth.currentUser();
			if (user) {
				posthog.identify(user.id, { email: user.email, name: user.name });
			} else {
				posthog.reset();
			}
		});
	}

	track(event: AnalyticsEvent, properties?: Record<string, unknown>): void {
		posthog.capture(event, properties);
	}
}
